// import { env } from "lib/env"
import mapboxgl from "mapbox-gl"
import {
  PARCEL_ZOOM,
  PARCEL_LAYER_NAME,
  BLOCKGROUP_LAYER_NAME,
  RIGHT_OF_WAY_LAYER_NAME,
  PARCEL_OUTLINE_LAYER_NAME,
  BLOCKGROUP_OUTLINE_LAYER_NAME,
  RIGHT_OF_WAY_OUTLINE_LAYER_NAME,
  MUNICIPALITY_OUTLINE_LAYER_NAME,
  BLOCKGROUP_ZOOM,
  MUNICIPALITY_LAYER_NAME,
  DEFAULT_ACTIVE_FACET,
  DEFAULT_PARCEL_ACTIVE_FACET,
  DEFAULT_MUNICIPALITY_ACTIVE_FACET,
  BLOCKGROUP_OUTLINE_HOVER_LAYER_NAME,
  PARCEL_OUTLINE_HOVER_LAYER_NAME,
  RIGHT_OF_WAY_OUTLINE_HOVER_LAYER_NAME,
  MUNICIPALITY_ZOOM,
  AGGREGATED_MUNICIPALITY_LAYER_NAME,
  SourceNames,
  MUNICIPALITY_SOURCE_ZOOM,
} from "app/constants"
import { getFillExpression, getOpacityExpression } from "app/utils/paint_utils"
import { Facet } from "data/facets"
import {
  BASE_LAYER_OPACITY,
  CITIES_BASE_LAYERS_TILES,
  HIDE_TREES_LAYER_FROM_LOCATION,
} from "app/features/regional-map/regional-map.constants"
import { getTileServerUrlFormatter } from "app/utils/map_utils"

export function addMapLayers(params: {
  map: mapboxgl.Map
  isSatellite: boolean
  facets: Facet[]
  cityId: string
  treesTilesetId?: string
  municipalitySlugs?: string[]
}) {
  const { map, isSatellite, facets, cityId, treesTilesetId, municipalitySlugs } = params

  const defaultParcelFacet = facets.find(
    (f) => f.field.type == "Area" && f.field.field === DEFAULT_PARCEL_ACTIVE_FACET
  )!

  const defaultBlockgroupFacet = facets.find(
    (f) => f.field.type == "Block Group" && f.field.field === DEFAULT_ACTIVE_FACET
  )!

  const defaultMunicipalityFacet = facets.find(
    (f) => f.field.type == "Municipality" && f.field.field === DEFAULT_MUNICIPALITY_ACTIVE_FACET
  )!

  const formatTileserverUrl = getTileServerUrlFormatter(cityId)
  const formatNationalTileserverUrl = getTileServerUrlFormatter("national")

  const municipalityFilter = municipalitySlugs
    ? ["in", ["get", "slug"], ["literal", municipalitySlugs]]
    : undefined

  if (treesTilesetId && !HIDE_TREES_LAYER_FROM_LOCATION.includes(cityId)) {
    map.addSource(SourceNames.TREE, {
      type: "vector",
      url: `mapbox://${treesTilesetId}`,
    })
  } else if (!HIDE_TREES_LAYER_FROM_LOCATION.includes(cityId)) {
    map.addSource(SourceNames.TREE, {
      type: "vector",
      tiles: [formatTileserverUrl(`${cityId}_trees`)],
      minzoom: PARCEL_ZOOM,
      maxzoom: PARCEL_ZOOM,
    })
  }

  if (CITIES_BASE_LAYERS_TILES[cityId]) {
    map.addSource("base_layer", {
      type: "vector",
      tiles: [formatTileserverUrl(`${CITIES_BASE_LAYERS_TILES[cityId]}`)],
      minzoom: BLOCKGROUP_ZOOM,
      maxzoom: 11,
    })
  }

  map.addSource(SourceNames.RIGHT_OF_WAY, {
    type: "vector",
    tiles: [formatTileserverUrl(`${cityId}_rows`)],
    minzoom: PARCEL_ZOOM,
    maxzoom: PARCEL_ZOOM,
  })

  map.addSource(SourceNames.PARCEL, {
    type: "vector",
    tiles: [formatTileserverUrl(`${cityId}_parcels`)],
    minzoom: PARCEL_ZOOM,
    maxzoom: PARCEL_ZOOM,
  })

  map.addSource(SourceNames.BLOCKGROUP, {
    type: "vector",
    tiles: [formatTileserverUrl(`${cityId}_blockgroups`)],
    minzoom: BLOCKGROUP_ZOOM,
    maxzoom: 11,
  })

  const municipalityFormatter =
    cityId === "toronto" ? formatTileserverUrl : formatNationalTileserverUrl
  map.addSource(SourceNames.MUNICIPALITY, {
    type: "vector",
    tiles: [
      municipalityFormatter(`${cityId === "toronto" ? "toronto" : "national"}_municipalities`),
    ],
    minzoom: 2,
    maxzoom: MUNICIPALITY_SOURCE_ZOOM,
  })

  const beforeLayer = isSatellite ? undefined : "admin-0-boundary-disputed"
  const beforePoi = isSatellite ? undefined : "poi-label"

  map.addLayer(
    {
      source: SourceNames.PARCEL,
      "source-layer": "data",
      id: PARCEL_LAYER_NAME,
      type: "fill",
      minzoom: PARCEL_ZOOM,
      paint: {
        "fill-color": getFillExpression(defaultParcelFacet),
        "fill-opacity": getOpacityExpression(defaultParcelFacet),
      },
    },
    beforePoi
  )

  map.addLayer(
    {
      source: SourceNames.RIGHT_OF_WAY,
      "source-layer": "data",
      id: RIGHT_OF_WAY_LAYER_NAME,
      type: "fill",
      minzoom: PARCEL_ZOOM,
      paint: {
        "fill-color": getFillExpression(defaultParcelFacet),
        "fill-opacity": getOpacityExpression(defaultParcelFacet),
      },
    },
    beforeLayer
  )

  if (!HIDE_TREES_LAYER_FROM_LOCATION.includes(cityId)) {
    map.addLayer({
      source: SourceNames.TREE,
      "source-layer": treesTilesetId ? "treesgeojsonl" : "data",
      id: "tree_canopy",
      type: "fill",
      minzoom: PARCEL_ZOOM,
      paint: {
        "fill-color": "#3F9A94",
        "fill-opacity": 0.15,
      },
    })

    map.addLayer({
      source: SourceNames.TREE,
      "source-layer": treesTilesetId ? "treesgeojsonl" : "data",
      id: "tree_canopy-outlines",
      type: "line",
      minzoom: PARCEL_ZOOM,
      paint: {
        "line-color": "#3F9A81",
        "line-width": 0.8,
      },
    })
  }

  if (CITIES_BASE_LAYERS_TILES[cityId]) {
    map.addLayer({
      source: "base_layer",
      "source-layer": "data",
      id: "base_layer",
      type: "fill",
      minzoom: 0,
      maxzoom: PARCEL_ZOOM,
      layout: {
        visibility: "none",
      },
      paint: {
        "fill-color": "#827671",
        "fill-opacity": BASE_LAYER_OPACITY,
      },
    })
  }

  map.addLayer(
    {
      source: SourceNames.BLOCKGROUP,
      "source-layer": "data",
      id: BLOCKGROUP_LAYER_NAME,
      type: "fill",
      minzoom: 0,
      maxzoom: PARCEL_ZOOM,
      paint: {
        "fill-color": getFillExpression(defaultBlockgroupFacet),
        "fill-opacity": getOpacityExpression(defaultBlockgroupFacet),
      },
    },
    beforePoi
  )

  map.addLayer(
    {
      source: SourceNames.RIGHT_OF_WAY,
      "source-layer": "data",
      id: RIGHT_OF_WAY_OUTLINE_LAYER_NAME,
      type: "line",
      minzoom: PARCEL_ZOOM,
    },
    beforePoi
  )

  map.addLayer(
    {
      source: SourceNames.MUNICIPALITY,
      "source-layer": "data",
      id: AGGREGATED_MUNICIPALITY_LAYER_NAME,
      type: "fill",
      minzoom: 2,
      maxzoom: MUNICIPALITY_ZOOM,
      paint: {
        "fill-color": "#33966D",
      },
      ...(municipalityFilter && { filter: municipalityFilter }),
    },
    beforePoi
  )

  map.addLayer(
    {
      source: SourceNames.MUNICIPALITY,
      "source-layer": "data",
      id: MUNICIPALITY_LAYER_NAME,
      type: "fill",
      minzoom: MUNICIPALITY_ZOOM,
      maxzoom: BLOCKGROUP_ZOOM,
      paint: {
        "fill-color": getFillExpression(defaultMunicipalityFacet),
        "fill-opacity": getOpacityExpression(defaultMunicipalityFacet),
      },
      ...(municipalityFilter && { filter: municipalityFilter }),
    },
    beforePoi
  )

  map.addLayer(
    {
      source: SourceNames.PARCEL,
      "source-layer": "data",
      id: PARCEL_OUTLINE_LAYER_NAME,
      type: "line",
      minzoom: PARCEL_ZOOM,
    },
    beforePoi
  )

  map.addLayer(
    {
      source: SourceNames.BLOCKGROUP,
      "source-layer": "data",
      id: BLOCKGROUP_OUTLINE_LAYER_NAME,
      type: "line",
      minzoom: BLOCKGROUP_ZOOM,
    },
    beforePoi
  )

  map.addLayer(
    {
      source: SourceNames.MUNICIPALITY,
      "source-layer": "data",
      id: MUNICIPALITY_OUTLINE_LAYER_NAME,
      type: "line",
      minzoom: MUNICIPALITY_ZOOM,
      maxzoom: 24,
      ...(municipalityFilter && { filter: municipalityFilter }),
    },
    beforePoi
  )

  map.addLayer({
    source: SourceNames.BLOCKGROUP,
    "source-layer": "data",
    id: BLOCKGROUP_OUTLINE_HOVER_LAYER_NAME,
    type: "line",
    minzoom: BLOCKGROUP_ZOOM,
  })

  map.addLayer({
    source: SourceNames.RIGHT_OF_WAY,
    "source-layer": "data",
    id: RIGHT_OF_WAY_OUTLINE_HOVER_LAYER_NAME,
    type: "line",
    minzoom: PARCEL_ZOOM,
  })

  map.addLayer({
    source: SourceNames.PARCEL,
    "source-layer": "data",
    id: PARCEL_OUTLINE_HOVER_LAYER_NAME,
    type: "line",
    minzoom: PARCEL_ZOOM,
  })
}
